import React, { useState } from 'react';
import { ClipboardList, RefreshCw, Check, Edit2 } from 'lucide-react';
import { CollapsiblePanel } from './CollapsiblePanel';
import SOAPNoteEditor from './SOAPNoteEditor';

interface ChiropracticMetric {
  timestamp: number;
  muscleTensionScore?: number;
  tissueElasticity?: number;
  triggerPointIntensity?: number;
  rangeOfMotion?: number;
  painResponse?: number;
  treatmentEffectiveness?: number;
}

interface ChiropracticSession {
  id: string;
  patientId: number;
  startTime: string;
  endTime: string;
  duration: number;
  metrics: ChiropracticMetric[];
  notes: string;
  selectedMetrics: string[];
}

interface ChiropracticPatient {
  id: number;
  first_name: string;
  last_name: string;
  diagnosis: string;
  sessions_count: number;
  last_visit: string;
}

interface SOAPNote {
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
}

interface SOAPNoteGeneratorProps {
  session: ChiropracticSession;
  patient: ChiropracticPatient;
}

const SOAPNoteGenerator: React.FC<SOAPNoteGeneratorProps> = ({ session, patient }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedNote, setGeneratedNote] = useState<SOAPNote | null>(null);
  const [editMode, setEditMode] = useState(false);
  
  // Helper function to extract pain level from notes
  const extractPainLevel = (notes: string): string => {
    const painRegex = /pain (?:rated |at |of |is |reports |)(?:a |)(\d+)\/10/i;
    const match = notes.match(painRegex);
    return match ? match[1] : "not reported";
  };
  
  // Generate a SOAP note based on chiropractic session data
  const generateNote = async () => {
    setIsGenerating(true);
    
    try {
      // Simulate AI processing delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Extract relevant chiropractic metrics for the note
      const firstMetric = session.metrics[0] || null;
      const lastMetric = session.metrics.length > 0 
        ? session.metrics[session.metrics.length - 1] 
        : null;
      
      // Calculate average chiropractic metrics if available
      const avgMuscleTension = session.metrics.reduce((sum, metric) => 
        sum + (metric.muscleTensionScore || 0), 0) / (session.metrics.length || 1);
      
      const avgTreatmentEffectiveness = session.metrics.reduce((sum, metric) => 
        sum + (metric.treatmentEffectiveness || 0), 0) / (session.metrics.length || 1);
      
      const avgTissueElasticity = session.metrics.reduce((sum, metric) => 
        sum + (metric.tissueElasticity || 0), 0) / (session.metrics.length || 1);
      
      const avgRangeOfMotion = session.metrics.reduce((sum, metric) => 
        sum + (metric.rangeOfMotion || 0), 0) / (session.metrics.length || 1);
      
      const avgTriggerPointIntensity = session.metrics.reduce((sum, metric) => 
        sum + (metric.triggerPointIntensity || 0), 0) / (session.metrics.length || 1);
      
      // Calculate changes throughout session
      const firstTension = firstMetric?.muscleTensionScore || 0;
      const lastTension = lastMetric?.muscleTensionScore || 0;
      const tensionReduction = firstTension - lastTension;
      
      const firstPain = firstMetric?.painResponse || 0;
      const lastPain = lastMetric?.painResponse || 0;
      const painImprovement = firstPain - lastPain;
      
      const firstROM = firstMetric?.rangeOfMotion || 0;
      const lastROM = lastMetric?.rangeOfMotion || 0;
      const romImprovement = lastROM - firstROM;
      
      const firstTriggerPoint = firstMetric?.triggerPointIntensity || 0;
      const lastTriggerPoint = lastMetric?.triggerPointIntensity || 0;
      const triggerPointReduction = firstTriggerPoint - lastTriggerPoint;
      
      // Extract pain level from notes
      const painLevel = extractPainLevel(session.notes);
      
      // Get the date and format it
      const sessionDate = new Date(session.startTime).toLocaleDateString();
      
      // Generate SOAP note based on chiropractic data
      const note: SOAPNote = {
        subjective: `Patient ${patient.first_name} ${patient.last_name} presented on ${sessionDate} for chiropractic soft tissue treatment. ${
          patient.diagnosis ? `Diagnosis: ${patient.diagnosis}. ` : ''
        }${
          painLevel !== "not reported" ? `Patient reports pain level at ${painLevel}/10. ` : 'Pain level not reported. '
        }${
          session.notes.includes('tension') ? 'Patient reports tension in treated area. ' : ''
        }${
          session.notes.includes('stiffness') ? 'Patient reports stiffness and reduced mobility. ' : ''
        }${
          session.notes.includes('improved') ? 'Patient reports improved comfort since last visit. ' : ''
        }Goals include pain reduction, improved tissue mobility, and restored function.`,
        
        objective: `Treatment duration: ${Math.floor(session.duration / 60)} minutes. ${
          avgMuscleTension ? `Average muscle tension: ${Math.round(avgMuscleTension)}/100 (${tensionReduction > 0 ? 'reduced by ' + Math.round(tensionReduction) + ' points' : 'minimal change'}). ` : ''
        }${
          avgTreatmentEffectiveness ? `Treatment effectiveness: ${Math.round(avgTreatmentEffectiveness)}%. ` : ''
        }${
          avgTissueElasticity ? `Tissue elasticity: ${avgTissueElasticity.toFixed(1)}mm average. ` : ''
        }${
          avgRangeOfMotion ? `Range of motion: ${Math.round(avgRangeOfMotion)}° average${romImprovement > 0 ? ' (improved by ' + Math.round(romImprovement) + '°)' : ''}. ` : ''
        }${
          avgTriggerPointIntensity ? `Trigger point intensity: ${avgTriggerPointIntensity.toFixed(1)}/10${triggerPointReduction > 0 ? ' (reduced by ' + triggerPointReduction.toFixed(1) + ' points)' : ''}. ` : ''
        }${
          painImprovement > 0 ? `Pain reduced by ${painImprovement.toFixed(1)} points during session. ` : ''
        }${session.notes.includes('pressure') ? 'Applied progressive pressure techniques with good tolerance. ' : ''}`,
        
        assessment: `Patient demonstrates ${
          avgTreatmentEffectiveness > 80 ? 'excellent' : avgTreatmentEffectiveness > 65 ? 'good' : 'fair'
        } response to soft tissue treatment. ${
          tensionReduction > 10 ? 'Significant muscle tension reduction noted, indicating positive tissue response. ' :
          tensionReduction > 5 ? 'Moderate improvement in muscle tension observed. ' :
          tensionReduction > 0 ? 'Slight improvement in muscle tension noted. ' :
          'Minimal change in muscle tension during session. '
        }${
          romImprovement > 10 ? 'Substantial range of motion improvement achieved. ' :
          romImprovement > 5 ? 'Moderate range of motion gains noted. ' :
          romImprovement > 0 ? 'Slight improvement in mobility observed. ' :
          'Range of motion maintained throughout session. '
        }${
          patient.diagnosis.includes('lower back') || patient.diagnosis.includes('lumbar') ? 'Lumbar region responding well to soft tissue mobilization. ' :
          patient.diagnosis.includes('neck') || patient.diagnosis.includes('cervical') ? 'Cervical region shows good response to treatment. ' :
          patient.diagnosis.includes('shoulder') ? 'Shoulder mobility and function improving with treatment. ' : 
          'Target area responding appropriately to treatment intervention. '
        }${
          avgTriggerPointIntensity > 7 ? 'Active trigger points require continued attention. ' :
          avgTriggerPointIntensity > 4 ? 'Trigger point activity moderately reduced. ' :
          'Trigger point activity significantly decreased. '
        }`,
        
        plan: `Continue with ${
          session.notes.includes('pressure') ? 'progressive pressure techniques' : 
          session.notes.includes('mobilization') ? 'soft tissue mobilization' :
          session.notes.includes('stretch') ? 'assisted stretching protocol' :
          'current soft tissue treatment protocol'
        } focusing on ${
          avgMuscleTension > 60 ? 'muscle tension reduction' : 
          avgTriggerPointIntensity > 6 ? 'trigger point deactivation' :
          avgRangeOfMotion < 70 ? 'range of motion improvement' :
          'maintaining current progress and functional gains'
        }. ${
          avgTreatmentEffectiveness < 70 ? 'Consider modifying treatment approach to improve effectiveness. ' :
          'Continue current treatment approach with good patient response. '
        }${
          painImprovement < 1 ? 'Monitor pain levels and adjust treatment intensity as needed. ' :
          'Patient demonstrating good pain reduction response. '
        }${
          avgRangeOfMotion < 70 ? 'Include range of motion exercises and stretching protocols. ' : ''
        }${
          avgMuscleTension > 70 ? 'Focus on muscle relaxation techniques and postural education. ' : ''
        }Home care instructions provided. Reassess progress in 1 week.`
      };
      
      setGeneratedNote(note);
    } catch (error) {
      console.error("Error generating SOAP note:", error);
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleSaveEdit = (editedNote: SOAPNote) => {
    setGeneratedNote(editedNote);
    setEditMode(false);
  };
  
  return (
    <CollapsiblePanel
      title="AI-Generated SOAP Note"
      icon={<ClipboardList className="w-6 h-6 text-indigo-400" />}
      defaultExpanded={true}
    >
      <div className="space-y-4">
        {!generatedNote ? (
          <div className="flex flex-col items-center justify-center space-y-4 p-6 bg-gray-800 rounded-lg">
            <p className="text-gray-300 text-center">
              Generate a structured SOAP note based on this chiropractic session's data
            </p>
            <button
              onClick={generateNote}
              disabled={isGenerating}
              className={`flex items-center px-4 py-2 rounded text-white ${
                isGenerating ? 'bg-indigo-700' : 'bg-indigo-600 hover:bg-indigo-500'
              }`}
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <ClipboardList className="w-5 h-5 mr-2" />
                  Generate SOAP Note
                </>
              )}
            </button>
          </div>
        ) : editMode ? (
          <SOAPNoteEditor 
            note={generatedNote}
            onSave={handleSaveEdit}
            onCancel={() => setEditMode(false)}
          />
        ) : (
          <div className="space-y-4">
            <div className="bg-gray-800 p-4 rounded-lg space-y-4">
              <div>
                <h3 className="text-sm text-gray-400 font-medium mb-1">SUBJECTIVE</h3>
                <p className="text-white">{generatedNote.subjective}</p>
              </div>
              
              <div>
                <h3 className="text-sm text-gray-400 font-medium mb-1">OBJECTIVE</h3>
                <p className="text-white">{generatedNote.objective}</p>
              </div>
              
              <div>
                <h3 className="text-sm text-gray-400 font-medium mb-1">ASSESSMENT</h3>
                <p className="text-white">{generatedNote.assessment}</p>
              </div>
              
              <div>
                <h3 className="text-sm text-gray-400 font-medium mb-1">PLAN</h3>
                <p className="text-white">{generatedNote.plan}</p>
              </div>
            </div>
            
            <div className="flex justify-between">
              <button
                onClick={() => setEditMode(true)}
                className="flex items-center text-sm px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </button>
              
              <button
                onClick={generateNote}
                className="flex items-center text-sm px-3 py-1 rounded bg-indigo-700 hover:bg-indigo-600 text-white"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Regenerate
              </button>
            </div>
            
            <div className="bg-gray-800 p-3 rounded-lg border border-gray-700">
              <div className="flex items-center text-sm text-gray-400">
                <Check className="w-4 h-4 text-green-500 mr-2" />
                SOAP note ready for your EHR system
              </div>
            </div>
          </div>
        )}
      </div>
    </CollapsiblePanel>
  );
};

export default SOAPNoteGenerator;